import type { Currency, ShippingDestination } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { Switch } from "@loot-os/ui/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { type ReactNode, useState } from "react";

import { amountInputLabel, amountInputStep } from "../lib/currency";
import {
  type DestinationIssue,
  type DestinationRow,
  ISO_COUNTRY_OPTIONS,
  moveRow,
  newRow,
  rowsDirty,
  toDestinations,
  toRows,
} from "../lib/shippingDestinations";
import { CountryCombobox } from "./CountryCombobox";

/**
 * The shipping destinations table (reboxed's Shipping Config app): one row per country with its
 * switch, cost and delivery estimate, reordered with the arrows because the order is what the
 * storefront shows. Renders as the children of a `CardContent`, so the table runs edge to edge
 * like every other table in a card. The parent owns saving; this holds the draft, so mount it
 * under a `key` that changes when the saved list does and the draft resets with it. Every save
 * is a full replace.
 */
export function ShippingDestinationsEditor({
  destinations,
  currency,
  canEdit,
  saving,
  onSave,
  footer,
}: {
  destinations: readonly ShippingDestination[];
  currency: Currency;
  canEdit: boolean;
  saving: boolean;
  onSave: (destinations: ShippingDestination[]) => void;
  /** Extra actions beside Save, such as "Use platform default". */
  footer?: ReactNode;
}) {
  const [rows, setRows] = useState<DestinationRow[]>(() => toRows(destinations, currency));
  const [issue, setIssue] = useState<DestinationIssue | null>(null);
  const used = new Set(rows.map((row) => row.countryCode).filter(Boolean));
  const dirty = rowsDirty(rows, destinations, currency);

  const update = (key: string, patch: Partial<DestinationRow>) => {
    setIssue(null);
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const submit = () => {
    const parsed = toDestinations(rows, currency);
    if (!parsed.ok) {
      setIssue(parsed.issue);
      return;
    }
    onSave(parsed.destinations);
  };

  const issueFor = (key: string, field: DestinationIssue["field"]) =>
    issue && issue.key === key && issue.field === field ? issue.message : null;

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[34%]">Country</TableHead>
            <TableHead>Enabled</TableHead>
            <TableHead>{amountInputLabel("Cost", currency)}</TableHead>
            <TableHead>Delivery estimate</TableHead>
            <TableHead className="w-28" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="py-6 text-center text-muted-foreground">
                No destinations: shoppers cannot save an address or ship anything until a country is
                added.
              </TableCell>
            </TableRow>
          )}
          {rows.map((row, index) => (
            <TableRow key={row.key}>
              <TableCell>
                <CountryCombobox
                  id={`dest-${row.key}-country`}
                  size="sm"
                  value={row.countryCode}
                  countries={ISO_COUNTRY_OPTIONS}
                  disabledCodes={used}
                  disabled={!canEdit}
                  onChange={(countryCode) => update(row.key, { countryCode })}
                />
                <FieldIssue message={issueFor(row.key, "countryCode")} />
              </TableCell>
              <TableCell>
                <Switch
                  aria-label="Enabled"
                  checked={row.enabled}
                  disabled={!canEdit}
                  onCheckedChange={(enabled) => update(row.key, { enabled })}
                />
              </TableCell>
              <TableCell>
                <Input
                  className="h-8 w-28 text-right"
                  aria-label={amountInputLabel("Cost", currency)}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step={amountInputStep(currency)}
                  value={row.cost}
                  disabled={!canEdit}
                  aria-invalid={issueFor(row.key, "cost") !== null}
                  onChange={(e) => update(row.key, { cost: e.target.value })}
                />
                <FieldIssue message={issueFor(row.key, "cost")} />
              </TableCell>
              <TableCell>
                <Input
                  className="h-8 min-w-48"
                  aria-label="Delivery estimate"
                  value={row.deliveryEstimate}
                  placeholder="1–3 weeks from withdrawal"
                  maxLength={100}
                  disabled={!canEdit}
                  aria-invalid={issueFor(row.key, "deliveryEstimate") !== null}
                  onChange={(e) => update(row.key, { deliveryEstimate: e.target.value })}
                />
                <FieldIssue message={issueFor(row.key, "deliveryEstimate")} />
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Move up"
                    disabled={!canEdit || index === 0}
                    onClick={() => setRows((current) => moveRow(current, index, -1))}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Move down"
                    disabled={!canEdit || index === rows.length - 1}
                    onClick={() => setRows((current) => moveRow(current, index, 1))}
                  >
                    <ArrowDown />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove"
                    disabled={!canEdit}
                    onClick={() => {
                      setIssue(null);
                      setRows((current) => current.filter((r) => r.key !== row.key));
                    }}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canEdit}
          onClick={() => setRows((current) => [...current, newRow(currency)])}
        >
          <Plus />
          Add country
        </Button>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {footer}
          <Button
            type="button"
            variant="ghost"
            disabled={!dirty || saving}
            onClick={() => {
              setIssue(null);
              setRows(toRows(destinations, currency));
            }}
          >
            Discard
          </Button>
          <Button type="button" disabled={!canEdit || !dirty || saving} onClick={submit}>
            {saving ? "Saving…" : "Save destinations"}
          </Button>
        </div>
      </div>
    </>
  );
}

/** The one thing wrong with a field, under it; nothing when there is nothing. */
function FieldIssue({ message }: { message: string | null }) {
  if (message === null) return null;
  return <p className="mt-1 text-xs text-destructive">{message}</p>;
}
